import { useState, useRef, useCallback } from 'react';
import { ChatMessage, ActionType, Mode, ModelId } from '../types';
import { streamMessage, KnowledgeBase } from '../services/ai/fastApiBackend';
import { getUserIdentity } from '../services/identity';

import { htmlToText } from '../utils/textProcessing';
import { getEmailBody, splitHtmlReply } from '../services/office/outlook';
import { fraudMeasuresContext } from '../prompts/emailPrompts';
import { technicalSpecs } from '../prompts/iphone15Spec';
import { KnowledgeBaseId } from '../types';
import { HostType } from '../types/office';
import { getExcelMetadata } from '../services/office/excel';


export function useChat(
  userName: string, 
  senderName: string, 
  mode: Mode,
  emailBodyText: string,
  updateEmailBodyText: () => Promise<string>,
  getCurrentContentText: () => Promise<string>,
  model: ModelId = 'gpt-4o-mini',
  selectedKnowledgeBase: KnowledgeBaseId = 'none',
  host: HostType = 'Unknown',
  getSelection?: () => Promise<string>,
  knowledgeBases: KnowledgeBase[] = []
) {
  const [chat, setChat] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const abortRef = useRef<AbortController | null>(null);


  // Initialize chat with welcome message
  const initializeChat = useCallback(() => {
  let content = `Hello${userName ? " " + userName : ""}! How can I help you today?`;

    setChat([{ 
      role: "assistant", 
      content: content
    }]);

  }, [userName]);

  // Stream AI message
  const handleStreamMessage = useCallback(async (
    _systemPrompt: string, // Not used - backend handles system prompt
    userActualContent: string,
    modelOverride?: ModelId,
    actionType?: ActionType,
    contextData?: Record<string, any>
  ) => {
    setIsStreaming(true);

    const controller = new AbortController();
    abortRef.current = controller;

    // System prompt building commented out - backend will handle system prompt
    // const knowledgeBaseContent = getKnowledgeBaseContent(selectedKnowledgeBase);
    // const knowledgeBaseLabel = selectedKnowledgeBase === 'legalDocuments' 
    //   ? 'Legal Documents' 
    //   : selectedKnowledgeBase === 'technicalSpecs'
    //   ? 'Technical Specs'
    //   : '';
    
    // // Build the system prompt in a modular way
    // let fullSystemPrompt = `My name is ${userName}. You are an microsoft office application assistant that helps users with their request.\n\n`;

    // fullSystemPrompt += `The current selected model is ${model}.\n\n`;

    // fullSystemPrompt += `Current date and time is ${new Date().toLocaleString()}.\n\n`;
    
    // // Application-specific context
    // fullSystemPrompt += `User is using ${host} application.\n\n`;
    
    // // Outlook-specific context (only for Outlook)
    // if (host === 'Outlook' && senderName) {
    //   fullSystemPrompt += `(Outlook specific): Recipient's name is ${senderName}, use these names if you draft email for user.\n\n`;
    // }
    
    // // Knowledge base reference (only if not 'none')
    // if (selectedKnowledgeBase !== 'none' && knowledgeBaseContent) {
    //   fullSystemPrompt += `Reference the ${knowledgeBaseLabel} if needed:\n${knowledgeBaseContent}\n\n`;
    // }
    
    // // Add the specific system prompt
    // fullSystemPrompt += `${systemPrompt}\n`;

    // Format chat history for backend before adding the new empty assistant message
    const chatHistory = chat.map(msg => ({
      content: msg.content,
      role: msg.role
    }));

    // Add empty assistant message that will be updated with streaming content
    setChat((prev) => [...prev, { role: "assistant", content: "" }]);
    
    try {
      // Build context object for backend - system prompt handled by backend
      const backendContext: Record<string, any> = {
        ...contextData,
        // system_prompt: fullSystemPrompt, // Backend handles system prompt
      };

      // Add application-specific context (sender_name for Outlook)
      // Note: This could be added to applicationContent if needed, but keeping at top level for now
      // as it's metadata rather than content
      if (host === 'Outlook' && senderName) {
        backendContext.sender_name = senderName;
      }

      // Selection is only included if user has actually highlighted text in the document
      // (handled in handleAction before calling handleStreamMessage)

      console.log(`[useChat] Sending to FastAPI backend - userContent length: ${userActualContent.length}, actionType: ${actionType}, chatHistory length: ${chatHistory.length}`);
      
      // Find the selected knowledge base object if not 'none'
      const selectedKnowledgeBaseObj = selectedKnowledgeBase !== 'none' 
        ? knowledgeBases.find(kb => kb.id === selectedKnowledgeBase) || null
        : null;
      
      // Get access token from MSAL (allow auto-popup for user-initiated actions)
      const identityResult = await getUserIdentity(true);
      if (!identityResult.identity?.rawAccessToken) {
        throw new Error("Failed to get access token for authentication");
      }
      const identity = identityResult.identity;
      
      // Call FastAPI backend - messages array contains only chat history and user message
      // System prompt and context are in the context object
      await streamMessage(
        identity.rawAccessToken, // Access token from MSAL
        "", // No system prompt in messages - it's in context
        chatHistory, // Pass formatted chat history
        userActualContent, // User's current message
        (assistantMessage) => {
          setChat((prev) => {
            const updated = [...prev];
            const lastIndex = updated.length - 1;
            if (updated[lastIndex]?.role === "assistant") {
              updated[lastIndex].content = assistantMessage;
            }
            return updated;
          });
        },
        () => setIsStreaming(false),
        controller.signal,
        modelOverride || model,
        host,
        actionType,
        backendContext,
        selectedKnowledgeBaseObj
      );
    } catch (error: any) {
      console.error("Error streaming message:", error);
      setIsStreaming(false);
      
      // If it's an insufficient tokens error, update the chat with the error message
      if (error?.isInsufficientTokens || error?.message === "INSUFFICIENT_TOKENS") {
        setChat((prev) => {
          const updated = [...prev];
          const lastIndex = updated.length - 1;
          if (updated[lastIndex]?.role === "assistant") {
            updated[lastIndex].content = "Insufficient tokens. Please contact support.";
          }
          return updated;
        });
      }
    }
  }, [userName, senderName, model, selectedKnowledgeBase, host, chat, knowledgeBases]);

  // Handle predefined actions and custom prompts
  const handleAction = useCallback(async (
    type: ActionType,
    customPrompt?: string,
    modelOverride?: ModelId
  ) => {
    let promptText = "";
    
    // IMPORTANT: Update content FIRST before adding user message to chat
    // This ensures we always use the latest content
    let currentContent = "";
    let currentHtmlBody = "";
    let replyOnlyText = "";
    let emailOnlyText = "";
    
    try {
      // Always refresh content before processing for all hosts
      // updateEmailBodyText now returns the content directly, so we can use it
      console.log("[useChat] Refreshing content before processing query...");
      const updatedContent = await updateEmailBodyText();
      
      // Use the returned content directly (avoids async state issues)
      // If updateEmailBodyText returns content, use it; otherwise fallback to getCurrentContentText
      if (updatedContent && updatedContent.length > 0) {
        currentContent = updatedContent;
      } else {
        currentContent = await getCurrentContentText();
      }
      console.log(`[useChat] Fresh content retrieved, length: ${currentContent.length}`);
      
      // For Outlook, also get HTML for HTML parsing in compose mode
      if (host === 'Outlook') {
        try {
          currentHtmlBody = await getEmailBody(Office.CoercionType.Html);
          
          // Parse HTML in compose mode for Outlook
          const { replyOnly, emailOnly } = splitHtmlReply(currentHtmlBody);
          replyOnlyText = htmlToText(replyOnly);
          emailOnlyText = htmlToText(emailOnly);
        } catch (error) {
          console.error("Failed to get Outlook HTML body:", error);
          // Fallback: use text content for both
          replyOnlyText = currentContent;
          emailOnlyText = "";
        }
      } else {
        // For Word, PowerPoint, and other hosts, use the plain text content
        replyOnlyText = currentContent;
        emailOnlyText = "";
      }
    } catch (error) {
      console.error("Failed to update content:", error);
      // Fallback to cached version
      currentContent = emailBodyText;
      replyOnlyText = emailBodyText;
      emailOnlyText = "";
    }
    
    // Set user message based on action type (AFTER getting fresh content)
    // Only include the prompt text, not the content (content is in context.applicationContent)
    if (type === "custom" && customPrompt?.trim()) {
      promptText = customPrompt.trim();
      setChat((prev) => [...prev, { role: "user", content: promptText }]);
      setInput("");
    } else {
      // For action buttons, add descriptive user message
      // PowerPoint-specific prompts
      if (host === "PowerPoint") {
        switch (type) {
          case "summarize": 
            promptText = "Summarize this presentation"; 
            break;
          case "notes": 
            promptText = "Generate a script"; 
            break;
          case "rewrite": 
            promptText = "Polish the presentation"; 
            break;
          default:
            promptText = ""; // Other actions not available for PowerPoint
            break;
        }
      } else if (host === "Word") {
        // Word-specific prompts
        switch (type) {
          case "summarize": 
            promptText = "Summarize this document"; 
            break;
          case "grammar": 
            promptText = "Check for grammar and spelling mistakes"; 
            break;
          default:
            promptText = ""; // Other actions not available for Word
            break;
        }
      } else if (host === "Excel") {
        // Excel-specific prompts
        switch (type) {
          case "summarize": 
            promptText = "Summarize selection"; 
            break;
          case "pivottable": 
            promptText = "Recommend a pivot table"; 
            break;
          default:
            promptText = ""; // Other actions not available for Excel
            break;
        }
      } else {
        // Outlook-specific prompts
        switch (type) {
          case "summarize": promptText = "Summarize this email."; break;
          case "actionItems": promptText = "List action items from the email."; break;
          case "replyOptions": promptText = "Suggest a reply"; break;
          case "grammar": promptText = "Check grammar and spelling mistakes"; break;
          case "review": promptText = mode === "read" ? 
                                 "Generate a reply draft" : 
                                 "Generate a template"; break;
          case "professional": promptText = "Polish my email"; break;
        }
      }
      setChat((prev) => [...prev, { role: "user", content: promptText }]);
    }

    // Use promptText directly as userContent (content is already in context.applicationContent)
    // No need to build contextText with content since backend has access to context
    const userContent = promptText;
    
    console.log(`[useChat] User content (without application content): ${userContent}`);
    
    // Build context data for backend
    const contextData: Record<string, any> = {};
    
    // Check if user has actually selected/highlighted text in the document
    // Only include textSelected if there's actually selected text (at top level)
    if (getSelection) {
      try {
        const textSelected = await getSelection();
        if (textSelected && textSelected.trim().length > 0) {
          contextData.textSelected = textSelected.trim();
          console.log(`[useChat] Selected text found, length: ${textSelected.trim().length}`);
        } else {
          console.log(`[useChat] No selected text found or selection is empty`);
        }
      } catch (error) {
        console.warn(`[useChat] Error getting selected text:`, error);
        // Don't add textSelected if there's an error
      }
    }
    
    // Add full application content as a string
    // For Outlook compose mode, use replyOnlyText; otherwise use currentContent
    const fullApplicationContent = host === 'Outlook' && mode === 'compose' 
      ? replyOnlyText 
      : currentContent;
    
    if (fullApplicationContent && fullApplicationContent.trim().length > 0) {
      contextData.applicationContent = fullApplicationContent;
    }
    
    // For Excel, add range and activeWorksheet to context
    if (host === 'Excel') {
      try {
        const excelMetadata = await getExcelMetadata();
        if (excelMetadata.range) {
          contextData.range = excelMetadata.range;
        }
        if (excelMetadata.activeWorksheet) {
          contextData.activeWorksheet = excelMetadata.activeWorksheet;
        }
        console.log(`[useChat] Excel metadata added to context - Range: ${excelMetadata.range}, ActiveWorksheet: ${excelMetadata.activeWorksheet}`);
      } catch (error) {
        console.warn(`[useChat] Error getting Excel metadata:`, error);
        // Continue without Excel metadata if there's an error
      }
    }
    
    handleStreamMessage("", userContent, modelOverride, type, contextData);
  }, [mode, emailBodyText, handleStreamMessage, updateEmailBodyText, getCurrentContentText, userName, host, getSelection]);

  return { 
    chat, 
    setChat, 
    input, 
    setInput, 
    isStreaming, 
    handleAction,
    initializeChat,
    abortRef
  };
}